import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BookOpen, GraduationCap, IndianRupee, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, LoadingGrid, StatCard, StatusBadge, inr } from "@/components/bits";
import api, { errMsg, fmtDate } from "@/lib/api";

const EMPTY_COURSE = { title: "", category: "HR", subcategory: "", description: "", duration: "", language: "English", mode: "online", price: "", discount: 0, certificate: true, skills: "", job_roles: "" };

export default function InstituteHub() {
  const [overview, setOverview] = useState(null);
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [courseModal, setCourseModal] = useState(false);
  const [course, setCourse] = useState(EMPTY_COURSE);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/institute/overview").then(({ data }) => setOverview(data)).catch(() => setOverview({}));
    api.get("/institute/courses").then(({ data }) => setCourses(data.courses)).catch(() => {});
    api.get("/institute/enrollments").then(({ data }) => setEnrollments(data.enrollments)).catch(() => {});
    api.get("/institute/payouts").then(({ data }) => setPayouts(data.payouts)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const addCourse = async () => {
    setBusy(true);
    try {
      await api.post("/institute/courses", {
        ...course, price: Number(course.price), discount: Number(course.discount || 0),
        skills: course.skills.split(",").map((s) => s.trim()).filter(Boolean),
        job_roles: course.job_roles.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Course submitted for admin review");
      setCourseModal(false);
      setCourse(EMPTY_COURSE);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const complete = async (id) => {
    try {
      const { data } = await api.post(`/institute/enrollments/${id}/complete`);
      toast.success(`Certificate ${data.cert_id} issued`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!overview) return <LoadingGrid count={4} />;
  const approved = overview.institute?.status === "approved";

  return (
    <div data-testid="institute-hub">
      {!approved && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-8" data-testid="institute-pending-banner">
          <p className="font-heading font-semibold text-amber-900">Institute verification {overview.institute?.status}</p>
          <p className="text-sm text-amber-700 mt-1">Admin approval is required before your courses can be submitted and published.</p>
        </div>
      )}

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Courses" value={overview.courses ?? 0} icon={BookOpen} />
        <StatCard label="Enrollments" value={overview.enrollments ?? 0} icon={Users} />
        <StatCard label="Completed" value={overview.completed ?? 0} icon={GraduationCap} />
        <StatCard label="Net Payout" value={inr(overview.net_payout)} icon={IndianRupee} accent />
      </div>

      <Tabs defaultValue="courses" className="mt-8">
        <TabsList className="mb-6">
          <TabsTrigger value="courses" data-testid="tab-courses">Courses</TabsTrigger>
          <TabsTrigger value="enrollments" data-testid="tab-enrollments">Enrollments</TabsTrigger>
          <TabsTrigger value="payouts" data-testid="tab-payouts">Payouts</TabsTrigger>
        </TabsList>

        <TabsContent value="courses">
          <div className="flex justify-end mb-4">
            <Button onClick={() => setCourseModal(true)} disabled={!approved} className="rounded-full bg-[#0B1B3D]" data-testid="add-course-btn">
              Add Course
            </Button>
          </div>
          {courses.length === 0 ? (
            <EmptyState title="No courses yet" description="Add a course — it goes live after admin approval." testId="courses-empty" />
          ) : (
            <div className="grid md:grid-cols-2 gap-5">
              {courses.map((c) => (
                <div key={c.id} className="bg-white border border-border/60 rounded-xl p-6 card-hover" data-testid={`course-${c.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-heading font-semibold text-[#0B1B3D]">{c.title}</p>
                      <p className="text-xs text-muted-foreground mt-1">{c.category} · {c.mode} · {c.duration}</p>
                    </div>
                    <StatusBadge status={c.status} />
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-border/60 text-sm">
                    <span className="font-bold">{inr(c.price - (c.discount || 0))}</span>
                    <span className="text-xs text-muted-foreground">{c.enrollments} enrollments</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="enrollments">
          {enrollments.length === 0 ? (
            <EmptyState title="No enrollments yet" description="Candidates who purchase your courses will appear here." testId="enrollments-empty" />
          ) : (
            <div className="space-y-3">
              {enrollments.map((e) => (
                <div key={e.id} className="bg-white border border-border/60 rounded-xl p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`enrollment-${e.id}`}>
                  <div>
                    <p className="font-semibold text-sm text-[#0B1B3D]">{e.learner_name}</p>
                    <p className="text-xs text-muted-foreground mt-1">{e.course_title} · {fmtDate(e.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={e.status} />
                    {e.status === "enrolled" && (
                      <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => complete(e.id)} data-testid={`complete-${e.id}`}>
                        Mark Complete & Issue Certificate
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="payouts">
          <div className="grid sm:grid-cols-3 gap-5 mb-6">
            <StatCard label="Gross Revenue" value={inr(overview.revenue_gross)} icon={IndianRupee} />
            <StatCard label="Job Spring Commission" value={inr(overview.commission)} icon={IndianRupee} />
            <StatCard label="Net Payout" value={inr(overview.net_payout)} icon={IndianRupee} accent />
          </div>
          {payouts.length === 0 ? (
            <EmptyState title="No payouts yet" description="Payouts are created per enrollment and become eligible on course completion." testId="payouts-empty" />
          ) : (
            <div className="space-y-3">
              {payouts.map((p) => (
                <div key={p.id} className="bg-white border border-border/60 rounded-xl p-5 flex items-center justify-between gap-4" data-testid={`payout-${p.id}`}>
                  <div>
                    <p className="font-semibold text-sm text-[#0B1B3D]">{p.ref?.course_title}</p>
                    <p className="text-xs text-muted-foreground mt-1">Gross {inr(p.gross)} · commission {p.commission_pct}% · {fmtDate(p.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-heading font-bold">{inr(p.amount)}</span>
                    <StatusBadge status={p.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={courseModal} onOpenChange={setCourseModal}>
        <DialogContent aria-describedby={undefined} className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="course-dialog">
          <DialogHeader><DialogTitle>Add course</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Course title *</Label><Input value={course.title} onChange={(e) => setCourse({ ...course, title: e.target.value })} className="mt-1.5" data-testid="course-title" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={course.category} onValueChange={(v) => setCourse({ ...course, category: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="course-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["HR", "Sales", "IT", "Finance", "Marketing", "Operations"].map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Mode</Label>
                <Select value={course.mode} onValueChange={(v) => setCourse({ ...course, mode: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="online">Online</SelectItem>
                    <SelectItem value="offline">Offline</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Duration</Label><Input value={course.duration} onChange={(e) => setCourse({ ...course, duration: e.target.value })} placeholder="6 weeks" className="mt-1.5" /></div>
              <div><Label>Price (₹) *</Label><Input type="number" value={course.price} onChange={(e) => setCourse({ ...course, price: e.target.value })} className="mt-1.5" data-testid="course-price" /></div>
              <div><Label>Discount (₹)</Label><Input type="number" value={course.discount} onChange={(e) => setCourse({ ...course, discount: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Skills taught</Label><Input value={course.skills} onChange={(e) => setCourse({ ...course, skills: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div><Label>Mapped job roles</Label><Input value={course.job_roles} onChange={(e) => setCourse({ ...course, job_roles: e.target.value })} className="mt-1.5" placeholder="HR Executive, Payroll Specialist" /></div>
            <div><Label>Description</Label><Textarea rows={3} value={course.description} onChange={(e) => setCourse({ ...course, description: e.target.value })} className="mt-1.5" data-testid="course-description" /></div>
            <Button onClick={addCourse} disabled={busy || !course.title || !course.price} className="w-full rounded-full bg-[#0B1B3D]" data-testid="course-submit-btn">
              {busy ? "Submitting…" : "Submit for Admin Review"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
